import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Box, Button, Paper, TextField, Typography, Alert, CircularProgress, Container } from '@mui/material';
import { useLocation, useParams } from 'react-router-dom';
import { dltFormAPI, dltFormPublicAPI } from '../../services/api';
import { Paperclip, UserRound, Check } from 'lucide-react';
import colors from '../../../../styles/colors';
import AttachmentList, { AttachmentUploadButton } from '../AttachmentPreview';

interface DLTFormInputs {
  attachments_url: string[];
  sender_name: string;
  provider_email: string;
}

const DLT_DOC_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.csv,application/pdf';
const DLT_TEMPLATE_FOLDER = 'journey-requests/dlt-templates';

const T = {
  coral: colors.primaryMain,
  coralDark: colors.primaryDark,
  coralTint: colors.primaryLight,
  ink: colors.textPrimary,
  sub: colors.textSecondary,
  faint: colors.grey500,
  border: colors.grey200,
  bg: colors.grey50,
  green: colors.successMain,
  greenTint: colors.successLight,
  error: colors.errorMain,
  errorTint: colors.errorLight,
};

const DLTForm: React.FC = () => {
  const params = useParams<{ requestId?: string }>();
  const requestId = params.requestId;
  const { pathname } = useLocation();
  const isPublic = pathname.startsWith('/dlt-forms/');

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting, isLoading },
    reset,
    setValue,
    getValues,
  } = useForm<DLTFormInputs>({
    mode: 'onBlur',
    defaultValues: {
      attachments_url: [],
      sender_name: '',
      provider_email: '',
    },
  });

  const attachments = watch('attachments_url');
  const [apiError, setApiError] = React.useState('');
  const [success, setSuccess] = React.useState(false);

  useEffect(() => {
    const loadForm = async () => {
      if (!requestId) return;

      try {
        const formResponse = isPublic
          ? await dltFormPublicAPI.getForm(requestId)
          : await dltFormAPI.getFormStatus(requestId);

        const formData = formResponse.data;

        if (formData.metadata) {
          reset({
            attachments_url: formData.metadata.attachments_url || [],
            sender_name: formData.metadata.sender_name || '',
            provider_email: formData.metadata.provider_email || '',
          });
        }
        setApiError('');
      } catch (err: any) {
        console.error('Failed to load DLT form:', err);
        setApiError(
          err.response?.status === 500
            ? 'Server error. Please contact support.'
            : err.response?.data?.message || 'Failed to load form.'
        );
      }
    };

    loadForm();
  }, [requestId, reset, isPublic]);

  const onSubmit = async (data: DLTFormInputs) => {
    if (!requestId) {
      setApiError('Request ID is required');
      return;
    }

    if (!data.attachments_url.length) {
      setApiError('Attach the registered DLT template file before submitting.');
      return;
    }

    try {
      setApiError('');
      const payload = {
        attachments_url: data.attachments_url,
        sender_id: data.sender_name,
        sender_name: data.sender_name,
        provider_email: data.provider_email,
      };

      await (isPublic
        ? dltFormPublicAPI.submitForm(requestId, payload)
        : dltFormAPI.submitForm(requestId, payload));
      setSuccess(true);
      setTimeout(() => setSuccess(false), 5000);
    } catch (err: any) {
      console.error('Form submission failed:', err);
      setApiError(err.response?.data?.message || 'Failed to submit form.');
    }
  };

  const addUploaded = (urls: string[]) => {
    setValue('attachments_url', [...getValues('attachments_url'), ...urls], { shouldValidate: true });
    setApiError('');
  };

  const removeAttachment = (index: number) => {
    setValue(
      'attachments_url',
      getValues('attachments_url').filter((_, i) => i !== index),
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: T.bg }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography>Loading form...</Typography>
        </Box>
      </Box>
    );
  }

  if (success) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Paper sx={{ maxWidth: 480, textAlign: 'center', p: '44px 40px', boxShadow: '0 8px 30px rgba(23,24,28,0.08)' }}>
          <Box sx={{
            width: 56, height: 56, borderRadius: '50%', bgcolor: T.greenTint,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mb: 2
          }}>
            <Check size={26} color={T.green} strokeWidth={3} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
            Template submitted
          </Typography>
          <Typography sx={{ color: T.sub, mb: 3, lineHeight: 1.6 }}>
            Your DLT template has been submitted successfully. It now shows up against the SMS channel
            on the campaign request, and updates will be sent to your provider email.
          </Typography>
          <Button
            onClick={() => setSuccess(false)}
            variant="outlined"
            sx={{ borderColor: T.border, color: T.ink, '&:hover': { bgcolor: T.bg } }}
          >
            Back to form
          </Button>
        </Paper>
      </Box>
    );
  }

  const Label = ({ children, required, optional }: { children: React.ReactNode; required?: boolean; optional?: boolean }) => (
    <Typography sx={{
      fontSize: '12.5px', fontWeight: 700, color: T.ink, mb: 0.75, display: 'flex', gap: 1, alignItems: 'center'
    }}>
      {children}
      {required && <span style={{ color: T.coral }}>*</span>}
      {optional && <span style={{ color: T.faint, fontWeight: 500 }}>(optional)</span>}
    </Typography>
  );

  const Section = ({ Icon, title, sub, children }: { Icon: any; title: string; sub?: string; children: React.ReactNode }) => (
    <Box sx={{ mt: 3.5 }}>
      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{
          width: 32, height: 32, borderRadius: 1.125, bgcolor: T.bg,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
        }}>
          <Icon size={15} color={T.sub} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: '14.5px', fontWeight: 800, color: T.ink }}>{title}</Typography>
          {sub && <Typography sx={{ fontSize: '12.5px', color: T.sub, mt: 0.25 }}>{sub}</Typography>}
        </Box>
      </Box>
      {children}
    </Box>
  );

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      fontSize: '13.5px',
      bgcolor: '#fff',
      '& fieldset': { borderColor: T.border },
      '&:hover fieldset': { borderColor: T.border },
      '&.Mui-focused fieldset': {
        borderColor: T.coral,
        boxShadow: `0 0 0 3px ${T.coralTint}`
      }
    },
    '& .MuiFormHelperText-root': {
      fontSize: '12px',
      color: T.faint,
      mt: 0.625
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: T.bg, p: { xs: 2, md: '44px 16px' } }}>
      <Container maxWidth="md">
        <Paper sx={{
          bgcolor: '#fff', border: `1px solid ${T.border}`, borderRadius: '18px',
          p: { xs: '20px', sm: '30px 36px' }, boxShadow: '0 4px 20px rgba(23,24,28,0.05)'
        }}>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.3px', mb: 0.75 }}>
            DLT Template Submission
          </Typography>
          <Typography sx={{ fontSize: '13px', color: T.sub }}>
            DLT registration applies to SMS only. Tell us who the sender is and attach the registered
            template file.
          </Typography>

          {apiError && <Alert severity="error" sx={{ mt: 2 }}>{apiError}</Alert>}

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Sender identity */}
            <Section Icon={UserRound} title="Sender information" sub="How SMS messages appear to customers, and where we send updates.">
              <Box sx={{ display: 'flex', gap: 1.75, flexWrap: 'wrap' }}>
                <Controller
                  name="sender_name"
                  control={control}
                  rules={{
                    validate: (val) => {
                      if (!val) return true;
                      return /^[A-Z]{6}$/.test(val) || 'Must be exactly 6 uppercase letters (e.g., CASART).';
                    }
                  }}
                  render={({ field }) => (
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Label optional>Sender ID</Label>
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="CASART"
                        size="small"
                        disabled={isSubmitting}
                        error={!!errors.sender_name}
                        helperText={errors.sender_name?.message || 'Optional. 6-letter DLT registered header.'}
                        onChange={(e) => {
                          const val = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 6);
                          field.onChange(val);
                        }}
                        sx={textFieldSx}
                      />
                    </Box>
                  )}
                />
                <Controller
                  name="provider_email"
                  control={control}
                  rules={{
                    required: 'Email is required.',
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Enter a valid email address.'
                    }
                  }}
                  render={({ field }) => (
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Label required>Provider Email</Label>
                      <TextField
                        {...field}
                        fullWidth
                        type="email"
                        placeholder="you@casaretail.ai"
                        size="small"
                        disabled={isSubmitting}
                        error={!!errors.provider_email}
                        helperText={errors.provider_email?.message || 'For approval updates and queries.'}
                        sx={textFieldSx}
                      />
                    </Box>
                  )}
                />
              </Box>
            </Section>

            {/* Template file */}
            <Section
              Icon={Paperclip}
              title="Template file"
              sub="Upload the registered DLT template — the file should carry the template ID, the body content and any attachments that go with it. PDF, Word or Excel."
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: attachments.length ? 2 : 0 }}>
                <AttachmentUploadButton
                  onUploaded={addUploaded}
                  disabled={isSubmitting}
                  accept={DLT_DOC_ACCEPT}
                  label="Upload template file"
                  folderPath={DLT_TEMPLATE_FOLDER}
                  uploader={
                    isPublic && requestId
                      ? (files) => dltFormPublicAPI.uploadAttachments(requestId, files)
                      : undefined
                  }
                />
                <Typography sx={{ fontSize: '12px', color: T.faint }}>
                  PDF, DOC/DOCX, XLS/XLSX or CSV — up to 5 MB each.
                </Typography>
              </Box>

              {attachments.length ? (
                <AttachmentList attachments={attachments} onRemove={removeAttachment} />
              ) : (
                <Box sx={{
                  mt: 2, p: 2, border: `1px dashed ${T.border}`, borderRadius: '10px',
                  bgcolor: T.bg, textAlign: 'center'
                }}>
                  <Typography sx={{ fontSize: '12.5px', color: T.faint }}>
                    No file attached yet.
                  </Typography>
                </Box>
              )}
            </Section>

            {/* Submit */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mt: 3.75, flexWrap: 'wrap' }}>
              <Button
                type="submit"
                disabled={isSubmitting}
                variant="contained"
                sx={{
                  flex: 1, minWidth: 200, bgcolor: T.coral,
                  color: colors.primaryContrastText, borderRadius: '12px', p: '13px 20px', fontSize: '14px',
                  fontWeight: 800, boxShadow: `0 2px 8px color-mix(in srgb, ${T.coral} 35%, transparent)`,
                  '&:hover': { bgcolor: T.coralDark },
                  '&:disabled': { bgcolor: T.coralTint }
                }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit for DLT approval'}
              </Button>
            </Box>
            <Typography sx={{ mt: 1.5, fontSize: '12px', color: T.faint, textAlign: 'center' }}>
              Submissions go to your DLT operator for approval — typically 1–2 business days.
            </Typography>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default DLTForm;
